const normalizeIdentifier = (value) => {
  if (typeof value === "string") return value.trim();
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

// The public route is /p/:username, but the endpoint behind it resolves a
// Business slug before it falls back to a personal username (see
// getProfessionalProfileByUsername in server/controllers/professional.js).
// An owner working inside a workspace therefore has to link to that
// workspace's slug; the personal username is only correct for a solo
// professional with no active business.
export const resolveBookingPageIdentifier = (business, professional) => {
  const businessSlug = normalizeIdentifier(business?.slug);
  if (businessSlug) return businessSlug;

  const username = normalizeIdentifier(professional?.username);
  if (username) return username;

  return normalizeIdentifier(professional?._id || professional?.id);
};

export const resolveBookingPagePath = (business, professional) => {
  const identifier = resolveBookingPageIdentifier(business, professional);
  return identifier ? `/p/${identifier}` : "";
};

export const resolveBookingPagePreviewPath = (business, professional) => {
  const path = resolveBookingPagePath(business, professional);
  return path ? `${path}?preview=1` : "";
};

// The link that opens one service on the booking page. It is the only way a
// client reaches a hidden service, since hidden ones are left off the list.
export const resolveServiceLinkPath = (business, professional, serviceId) => {
  const path = resolveBookingPagePath(business, professional);
  const id = normalizeIdentifier(serviceId);
  return path && id ? `${path}?service=${encodeURIComponent(id)}` : "";
};

// The booking link is the <value> in haastia.com/p/<value>. The server
// (validateUsernameFormat in server/utils/username.js) takes 3–30 lowercase
// letters and numbers with single hyphens between them.
export const BOOKING_LINK_PREFIX = "haastia.com/p/";
export const BOOKING_LINK_MIN_LENGTH = 3;
export const BOOKING_LINK_MAX_LENGTH = 30;

const toBookingLinkCharacters = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_.]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+/, "");

// Cleans what a pro types into the booking-link field. A trailing hyphen is
// left alone, since they may be about to type the next word; the server
// refuses it if it is still there on save.
export const normalizeBookingLinkInput = (value) =>
  toBookingLinkCharacters(value).slice(0, BOOKING_LINK_MAX_LENGTH);

// A booking link made from the business name, e.g. "Maya Torres Studio" ->
// "maya-torres-studio". Empty when the name has too little to work with.
export const suggestBookingLink = (businessName) => {
  const suggestion = toBookingLinkCharacters(businessName)
    .slice(0, BOOKING_LINK_MAX_LENGTH)
    .replace(/-+$/, "");
  return suggestion.length >= BOOKING_LINK_MIN_LENGTH ? suggestion : "";
};

// Reads the service a ?service=<id> link names. Only a single service id
// counts; anything else is treated as no link, so the page opens as usual.
const SERVICE_ID_PATTERN = /^[a-f0-9]{24}$/i;

export const readServiceLinkId = (search) => {
  const values = new URLSearchParams(search || "").getAll("service");
  if (values.length !== 1) return "";
  const id = values[0].trim();
  return SERVICE_ID_PATTERN.test(id) ? id.toLowerCase() : "";
};

export default resolveBookingPagePath;
